import { AwsGateCallbacks, AwsGateError, AwsGateRequest } from "src/types";

import { useAwsGateError } from "./aws-gate-error.hook";
import { useAwsGateLoader } from "./aws-gate-loader.hook";
import { useAwsGateRequest } from "./aws-gate-request.hook";
import Cookies from "js-cookie";
import { GET_TOKEN_BODY, GET_TOKEN_HEADER, useGetIdToken } from "../terminals";

const retryGetToken = async (getIdToken: any) => {
  let result = false;

  await getIdToken(
    {
      headers: GET_TOKEN_HEADER,
      data: GET_TOKEN_BODY,
    },
    {
      onSuccess: (response: any) => {
        if (
          response?.AuthenticationResult?.TokenType &&
          response?.AuthenticationResult?.IdToken
        ) {
          Cookies.set(
            "IdToken",
            `${response?.AuthenticationResult?.TokenType} ${response?.AuthenticationResult?.IdToken}`
          );

          result = true;
        } else {
          result = false;
        }
      },
      onFailure: () => {
        result = false;
      },
    }
  );

  return result;
};

export const useAws = <T, D>(gateId: string) => {
  const { doRequest } = useAwsGateRequest<T, D>(gateId);
  const { gateError, saveGateError, removeGateError } = useAwsGateError(gateId);
  const { idle, pending, toIdle, toPending } = useAwsGateLoader(gateId);
  const { crossGate: getIdToken } = useGetIdToken();

  const crossGate = async (
    request: AwsGateRequest<T>,
    {
      onSuccess,
      onFailure = saveGateError,
      onIdle = toIdle,
      onPending = toPending,
    }: AwsGateCallbacks<D>
  ) => {
    try {
      const requestWithCookie = {
        ...request,
        headers: {
          ...request.headers,
          Authorization: Cookies.get("IdToken"),
        },
      };
      onPending?.();
      onSuccess?.(await doRequest(requestWithCookie));
    } catch (err: unknown) {
      console.log("AWS HOOK - ERROR:", err);
      const castedError = err as AwsGateError;

      const isAuthError =
        castedError.response?.data.message === "Unauthorized" ||
        castedError.response?.data.message === "The incoming token has expired";

      if (isAuthError) {
        try {
          const result = await retryGetToken(getIdToken);

          if (result) {
            const retriedRequest = {
              ...request,
              headers: {
                ...request.headers,
                Authorization: Cookies.get("IdToken"),
              },
            };
            const retriedResponse = await doRequest(retriedRequest);
            onSuccess?.(retriedResponse);
            return;
          }
        } catch (err) {
          console.log(err);
        }
      }

      onFailure?.(castedError);
    } finally {
      onIdle?.();
    }
  };

  return { gateError, removeGateError, idle, pending, crossGate };
};
